// data/zones/zone4.ts — THE GLOAMING MINES.
// DATA ONLY. Numbers, tables, ASCII maps. The behaviour is content/zones/zone4.ts
// (the darkness mask, the brazier economy, the Unlit, four brains) and
// content/bosses/boss4.ts.
//
// DESIGN §5 Zone 4: "sight is a resource — the dark hides everything but tells,
// light is fuel you spend and defend, and something walks the dark that weapons
// cannot touch."
//
// The anti-reskin contract for this zone is *sight*. Zone 1 asks where you stand
// relative to the sources; zone 2 asks where you stand relative to the floor;
// zone 3 asks what you leave lying down. Zone 4 asks what you can see — and the
// answer is a resource you spend (brazier fuel, 20s a light), defend (Lampeaters
// eat it) and route around (the Gloom fills at 2x in the dark and 0.5x in
// brazier light, so light management IS clock management). On top of that walks
// one thing weapons do nothing to.

import type { EnemyDef, GeneratorDef, ProjectileDef, RoomTemplate, ZoneConfig, ZonePalette } from '../../types'
import { ZONE1_ENEMIES } from './zone1'

// ---------------------------------------------------------------- palette
// Near-black deep workings. Blacks and deep blue-greys for the rock, warm lamp
// gold for anything burning, pale ore cyan for the veins. torches: 0 is not a
// stylistic choice here — the darkness IS the zone (ZonePalette.torches, D-016).

export const ZONE4_PALETTE: ZonePalette = {
  // The floor is only ever seen inside a light, so it can afford to be warm-ish
  // grey-blue: against the mask it reads as "the circle you can stand in".
  floor: '#20222c',
  floorAlt: '#282b37',
  wall: '#3b4152',
  wallEdge: '#6d7690',
  accent: '#7fe6ff',
  glow: '#ffc46b',
  bg: '#04050a',
  torches: 0,
}

/** Zone-4 specific colours the mechanic and the boss paint with. */
export const ZONE4_COLORS = {
  /** The mask itself: not pure black — deep blue-black, so silhouettes read. */
  dark: '#04050b',
  lamp: '#ffc46b',
  lampHot: '#fff0c4',
  ember: '#ff8a3c',
  emberDead: '#4a3a34',
  ore: '#7fe6ff',
  oreDim: '#2b5a6b',
  rail: '#2e3342',
  railHi: '#565f75',
  strut: '#231d1a',
  strutHi: '#4a3a2c',
  /** The Unlit: two pale eyes and a body darker than the dark. */
  unlitBody: '#000104',
  unlitEye: '#cfe4ff',
  /** Readability law: homing reads violet. */
  homing: '#b98cff',
  moth: '#c9a9e8',
  delver: '#6b6152',
  plume: '#4a4032',
} as const

// ---------------------------------------------------------------- the light
// The zone's whole grammar in one block. Every radius here is world px (TILE=32).
//
// FLAGGED (lampRadius): DESIGN says "~4.5 tiles". 4.5 * 32 = 144. The meta node
// Lampwright (unlocks.ts, x1.25) and the relic Moth-Eaten Cloak (relics.ts,
// x1.3) both multiply it, and they stack — a fully-invested Warden carries a
// 234px lamp, which is a real build payoff and still less than a brazier's reach
// plus its fuel. The lamp never makes the Unlit stop; only braziers do.

export const ZONE4_LIGHT = {
  /** DESIGN §5 Zone 4: the Warden's lamp is ~4.5 tiles. */
  lampRadius: 144,
  /** Fraction of the radius that is full brightness before the falloff starts. */
  lampCore: 0.5,

  brazier: {
    /** Lit reach, world px (5 tiles). Bigger than the lamp: a planted flag. */
    radius: 160,
    core: 0.55,
    /** DESIGN: burns ~20s. */
    burnSeconds: 20,
    /** Last seconds of the burn visibly gutter — the tell that it is going. */
    gutterSeconds: 3,
    /** How close the Warden must stand to press E (matches rooms.ts fixtures). */
    reach: 52,
    /** Combat rooms: E is a flick of the wrist. */
    lightSeconds: 0.3,
    /** DESIGN §5 Boss 4: in the arena the relight is a 3s channel — a commitment. */
    arenaLightSeconds: 3,
    /**
     * FLAGGED: braziers do not burn down in the Mother's chamber or in the
     * respite. In the arena the ONLY thing that puts a light out is the Mother
     * (or a Lampeater she sends), so every dark brazier in that room is legibly
     * her doing and the fight reads as a duel over the light rather than as two
     * unrelated clocks. In the respite it is simply the zone's one warm room.
     */
    arenaBurnSeconds: 1e9,
  },

  /**
   * DESIGN §5 Zone 4, Gloom relation: 2x in the dark, 0.5x in brazier light.
   *
   * `dark` = nothing in the room is burning · `lamp` = something is, but not on
   * you · `brazier` = you are standing in it. The state is a question about the
   * ROOM, not about the Warden's tile — the lamp is centred on the Warden, so a
   * tile-based reading would make `dark` unreachable and delete the zone's
   * headline rule. The full reasoning is in content/zones/zone4.ts updateGloom.
   */
  gloom: { dark: 2, lamp: 1, brazier: 0.5 },

  /** The floor art: ore veins per room, seeded off the room's own geometry. */
  ore: { perRoom: 46, glintSeconds: 2.6 },
} as const

// ---------------------------------------------------------------- the Unlit
// DESIGN §5 Zone 4: "a tall faceless miner-shade, walks slowly toward you at all
// times, immune to weapons. Repelled only by standing in brazier light (it halts
// at the edge), or an Emberburst potion (banishes it for 45s). Touch = 30 dmg +
// it withdraws briefly. Max one active; it does not enter the boss arena."

export const ZONE4_UNLIT = {
  defId: 'unlit',
  /** Room seconds before it walks in. Long enough to have made a plan. */
  spawnAtSeconds: 45,
  /** It enters from a dark edge at least this far from the Warden. */
  spawnMinDistance: 420,
  /** DESIGN: it walks. This is slower than the Warden at 150. */
  speed: 55,
  /** DESIGN: touch = 30 damage. */
  touchDamage: 30,
  /** …and it recoils this far, and stands off for this long. */
  recoilDistance: 80,
  recoilSeconds: 2,
  /** DESIGN: an Emberburst banishes it for 45s. */
  banishSeconds: 45,
  /** Emberburst reach against it — TUNING.player.potionRadius. */
  banishRadius: 260,
  /** Sinking / rising animations, seconds. */
  sinkSeconds: 0.9,
  riseSeconds: 1.2,
  /** The drone: no continuous-loop primitive exists in engine/audio.ts, so the
   *  cue's RATE carries the distance. Interval seconds at far range -> at touch. */
  droneFar: 2.6,
  droneNear: 0.42,
  droneRange: 620,
} as const

// ---------------------------------------------------------------- projectiles

export const ZONE4_PROJECTILES: ProjectileDef[] = [
  // Readability law (DESIGN §4): homing reads violet. The Deep Caster is the
  // Bonecaster's body carrying a different *behaviour class*, and the colour is
  // how the player is told before the first one lands.
  {
    id: 'deepbolt',
    kind: 'homing',
    speed: 205,
    damage: 9,
    radius: 6,
    color: ZONE4_COLORS.homing,
    lifetime: 4.2,
    /** Modest: it corrects, it does not track. A dash still shakes it. */
    homingTurn: 1.5,
  },
]

// ---------------------------------------------------------------- enemies
// Four bodies, four silhouettes: a hunched digger, a winged croucher, a tiny
// spiral, a tall robe. Three of them have opinions about light, which is the
// zone (DESIGN §4: one silhouette per role per zone).

const BONECASTER = ZONE1_ENEMIES.find((e) => e.id === 'bonecaster')!

export const ZONE4_ENEMIES: EnemyDef[] = [
  {
    id: 'delver',
    name: 'Delver',
    role: 'grunt',
    hp: 38,
    speed: 68,
    radius: 11,
    touchDamage: 9,
    ai: {
      custom: 'z4_delver',
      // the surface half: an ordinary telegraphed poke
      attackRange: 28,
      attackCooldown: 1.9,
      windup: 0.45,
      strikeArc: 1.5,
      strikeRange: 44,
      strikeDamage: 1.2,
      // the burrow: sink, travel as a visible plume, erupt on a telegraph
      burrowCooldown: 7.5,
      burrowMinRange: 90,
      burrowMaxRange: 330,
      sinkSeconds: 0.45,
      /** DESIGN: 2.5s under the floor. */
      burrowSeconds: 2.5,
      /** The plume crawls — it is a threat you can walk away from. */
      plumeSpeed: 76,
      /** DESIGN §4 counterplay law: 0.5s of telegraph before the eruption. */
      eruptWindup: 0.5,
      eruptRadius: 54,
      eruptDamage: 1.5,
      eruptRecover: 0.7,
    },
    sprite: { key: 'z4_delver', size: 30, params: { skin: '#6b6152', cloth: '#3a3128', lamp: '#ffc46b' } },
    emberValue: 2,
    goldChance: 0.24,
  },
  {
    id: 'lampeater',
    name: 'Lampeater',
    role: 'support',
    hp: 30,
    speed: 96,
    radius: 11,
    touchDamage: 6,
    ai: {
      custom: 'z4_lampeater',
      /** How close it gets to a brazier before it starts eating. */
      douseRange: 44,
      /** DESIGN: it douses over a channel, and the channel is telegraphed. */
      douseChannel: 1.5,
      /** Seconds before it will try another light. */
      douseCooldown: 2.4,
      /** With nothing lit it harasses: weak, telegraphed darts. */
      harassRange: 150,
      harassCooldown: 2.6,
      harassWindup: 0.4,
      harassSpeed: 400,
      harassTime: 0.26,
      harassDamage: 1,
    },
    sprite: { key: 'z4_lampeater', size: 32, params: { wing: '#c9a9e8', body: '#3b2f4a', eye: '#ffe3a0' } },
    emberValue: 4,
    goldChance: 0.34,
  },
  {
    id: 'glintmoth',
    name: 'Glintmoth',
    role: 'swarm',
    hp: 12,
    speed: 134,
    radius: 8,
    touchDamage: 5,
    ai: {
      custom: 'z4_glintmoth',
      /**
       * Longer than the brightness band it flees (see below): a Glintmoth that
       * could not out-reach its own fear could never attack, never be reached by
       * a melee form, and never die — which would make any room holding one
       * unclearable. This number and `fleeAbove` are a matched pair.
       */
      lungeRange: 130,
      lungeCooldown: 1.6,
      windup: 0.26,
      lungeSpeed: 380,
      lungeTime: 0.22,
      /** Dazed at the landing point, in the light, hittable. The melee answer. */
      dazeSeconds: 0.45,
      /** How hard it is thrown outward when the light gets too bright. */
      fleeSpeedMul: 1.35,
      /**
       * It flees BRIGHTNESS (0..1), not the light's radius. At 0.75 against the
       * lamp's falloff that is roughly 90px, well inside the 130px lunge, so the
       * orbit sits at the dim rim and the dive can still happen.
       */
      fleeAbove: 0.75,
      /** Tangential drift: the spiral. */
      spiral: 0.85,
    },
    sprite: { key: 'z4_glintmoth', size: 22, params: { wing: '#c9a9e8', eye: '#9fe8ff' } },
    emberValue: 1,
    goldChance: 0.1,
  },
  {
    // DESIGN §5 Zone 4: "Bonecasters return as Deep Casters (violet homing bolts
    // now — same body, new projectile class)." The reuse is the point and is
    // stated: the silhouette is the promise "back rank, kill me", and the zone
    // changes what that promise costs. Re-dressed in miner's cloth via the same
    // painter's params — not a new body.
    ...BONECASTER,
    id: 'deep_caster',
    name: 'Deep Caster',
    hp: 26,
    speed: 64,
    ai: {
      ...BONECASTER.ai,
      keepRange: 235,
      minRange: 150,
      fireRange: 360,
      fireCooldown: 2.9,
      windup: 0.6,
      projectile: 'deepbolt',
    },
    sprite: { key: 'bonecaster', size: 30, params: { color: '#3f3a4e', glow: ZONE4_COLORS.homing } },
    emberValue: 4,
    goldChance: 0.32,
  },
  {
    // THE UNLIT. It is an enemy body only because that is the cheapest true way
    // to give it collision, a brain and a place in the world; every rule that
    // makes it what it is lives in content/zones/zone4.ts. It pays nothing, it
    // cannot be killed, and it never counts toward a room clear.
    id: 'unlit',
    name: 'The Unlit',
    role: 'stalker',
    hp: 99999,
    speed: ZONE4_UNLIT.speed,
    radius: 15,
    touchDamage: ZONE4_UNLIT.touchDamage,
    ai: { custom: 'z4_unlit' },
    sprite: { key: 'z4_unlit', size: 44, params: { eye: ZONE4_COLORS.unlitEye } },
    emberValue: 0,
    goldChance: 0,
  },
]

// ---------------------------------------------------------------- generators
// Three tiers (DESIGN §4). Each keeps the violet source-rift the other zones use
// so a generator is never mistaken for scenery — and in this zone the rift is
// also the only thing about them you can see from outside your lamp.

export const ZONE4_GENERATORS: GeneratorDef[] = [
  {
    id: 'gen_collapsedshaft',
    name: 'Collapsed Shaft',
    hp: 72,
    radius: 16,
    spawns: 'delver',
    interval: 4.6,
    burst: 1,
    cap: 4,
    sprite: { key: 'gen_collapsedshaft', size: 42, params: { rock: '#3b4152', timber: '#3a2b20' } },
    emberValue: 5,
  },
  {
    id: 'gen_cocooncluster',
    name: 'Moth-Cocoon Cluster',
    hp: 58,
    radius: 15,
    spawns: 'glintmoth',
    interval: 3.0,
    burst: 2,
    cap: 6,
    sprite: { key: 'gen_cocooncluster', size: 38, params: { silk: '#cbbfd8', glow: '#9fe8ff' } },
    emberValue: 4,
  },
  {
    id: 'gen_casteridol',
    name: 'Caster Idol',
    hp: 76,
    radius: 14,
    spawns: 'deep_caster',
    interval: 7.6,
    burst: 1,
    cap: 2,
    sprite: { key: 'gen_casteridol', size: 40, params: { stone: '#33384a', eye: ZONE4_COLORS.homing } },
    emberValue: 6,
  },
]

// ---------------------------------------------------------------- rooms
// 44x26 at 32px, like zones 1-3, so the camera reads the same. Six combat
// templates in three ordered difficulty groups — game/zones.ts splits the combat
// library into roomCount contiguous groups and draws one from each, so a run
// always reads easy -> choice -> gate:
//
//   group 1 (1 generator, braziers GENEROUS — 5-6):
//     z4-shaftmouth, z4-orevein
//     Learn the dark and the E key with more light than you need.
//   group 2 (2 generators, braziers 4, Lampeaters arrive):
//     z4-pumphouse, z4-cribgallery
//     Something is now actively eating what you light.
//   group 3 (3 generators, braziers 3, iron gate + key elite, the timed vault):
//     z4-deepworkings, z4-lampyard
//     Scarcity: three sources, one behind iron, and a vault door you cannot even
//     see until you have lit the brazier standing next to it. Both group-3
//     templates carry the vault so room 3 keeps its variety after the runner's
//     guaranteed-vault swap.
//
// Rooms read as mine galleries: strut rows, cribbing, machine bays, stopes. The
// rails and ore veins are floor ART, painted per-room by the mechanic, not tiles
// — they must not eat pathfinding.
//
// Glyphs: types.ts + D-011. 'B' brazier (this zone), '.' floor, '#' rock.

export const ZONE4_ROOMS: RoomTemplate[] = [
  {
    id: 'z4-shaftmouth',
    generators: ['gen_collapsedshaft'],
    patrols: ['delver', 'glintmoth'],
    map: [
      '############################################',
      '#..........................................#',
      '#...................F..................$...#',
      '#..@.......E...................B...........#',
      '#..........................................#',
      '#..........................................#',
      '#......##.....##.....##.....##.....##......#',
      '#......##.....##.....##.....##.....##......#',
      '#......##.....##.....##.....##.....##......#',
      '#..........................................#',
      '#..........................................#',
      '#..................##....##................#',
      '#...B.......B......##.G..##......B......B..#',
      '#..................##....##................#',
      '#..................##....##................#',
      '#....F.....................................#',
      '#..........................................#',
      '#......##.....##.....##.....##.....##......#',
      '#......##.....##.....##.....##.....##......#',
      '#......##.....##.....##.....##.....##......#',
      '#..........................................#',
      '#..........B...............................#',
      '#................E................E........#',
      '#..C.....................F.................#',
      '#..........................................#',
      '######################D#####################',
    ],
  },
  {
    id: 'z4-orevein',
    generators: ['gen_cocooncluster'],
    patrols: ['glintmoth', 'delver'],
    map: [
      '#########################D##################',
      '#..........................................#',
      '#..........................................#',
      '#..F......................E..............$.#',
      '#..................##......................#',
      '#.......##.........##.........##...........#',
      '#.......##.G..................##......B....#',
      '#..........................................#',
      '#................B......##.................#',
      '#.....B......##.........##.........##......#',
      '#............##....................##......#',
      '#..........................................#',
      '#..@............#######....#######........F#',
      '#...............#######....#######.........#',
      '#.......##....................##...........#',
      '#.......##....................##.....E.....#',
      '#...............F..........................#',
      '#.......................##.................#',
      '#.......................##.................#',
      '#............##....................##......#',
      '#............##....................##......#',
      '#..C................E....B............B....#',
      '#..................##......................#',
      '#..................##......................#',
      '#..........................................#',
      '############################################',
    ],
  },
  {
    id: 'z4-pumphouse',
    generators: ['gen_collapsedshaft', 'gen_cocooncluster'],
    patrols: ['delver', 'lampeater', 'glintmoth'],
    map: [
      '############################################',
      '#..........................................#',
      '#........................................C.#',
      '#....................E.....................#',
      '#....########...B..............########..B.#',
      '#....#......#..................#......#....#',
      '#....#..G...#.......##.........#..F...#....#',
      '#....#......#.......##.........#......#....#',
      '#....#......#.......##.........#......#....#',
      '#..........................................#',
      '#..........................................#',
      '#..........................................#',
      '#..@.................F..................E..#',
      '#.............E............................#',
      '#..........................................#',
      '#..........................................#',
      '#..........................................#',
      '#....#......#.......##.........#......#....#',
      '#....#......#.......##.........#......#....#',
      '#....#......#.......##.........#...G..#....#',
      '#....#...F..#..................#......#....#',
      '#....########.............B....########..$.#',
      '#...B................E.....................#',
      '#..........................................#',
      '#..........................................#',
      '#####################D######################',
    ],
  },
  {
    id: 'z4-cribgallery',
    generators: ['gen_cocooncluster', 'gen_casteridol'],
    patrols: ['glintmoth', 'deep_caster', 'lampeater'],
    map: [
      '############################################',
      '#..........................................#',
      '#.F.............E.........................C#',
      '#...........................B..............#',
      '#.....####....####....####....####....####.#',
      '#.....####....####....####....####....####.#',
      '#.....####....####....####....####....####.#',
      '#..........................................#',
      '#.........................................B#',
      '#...G......................................#',
      '#.....####....####....####....####....####.#',
      '#.....####....####....####....####....####.#',
      '#.@...####..B.####..F.####....####....####E#',
      '#...............E..........................#',
      '#..........................................#',
      '#..........................................#',
      '#.....####....####....####....####....###G.#',
      '#.....####....####....####....####....####.#',
      '#.....####....####....####....####....####.#',
      '#..........................................#',
      '#..........................................#',
      '#..........................................#',
      '#...........................B..............#',
      '#.$.....................E.................F#',
      '#..........................................#',
      '####################D#######################',
    ],
  },
  {
    id: 'z4-deepworkings',
    generators: ['gen_casteridol', 'gen_collapsedshaft', 'gen_cocooncluster'],
    patrols: ['delver', 'deep_caster', 'lampeater'],
    map: [
      '############################################',
      '#..........................................#',
      '#.............................##############',
      '#..@.............E............#...........##',
      '#........K....................#.........F.##',
      '#...........##.......##.......L...........##',
      '#...........##.......##.......L.....G.....##',
      '#...........##.......##.......L...........##',
      '#...B.........................#...........##',
      '#.............................#...........##',
      '#.............................##############',
      '#...........##.......##....................#',
      'D..F........##.......##....................#',
      '#...........##.......##...............B....#',
      '#......G..................E................#',
      '#..............F...........................D',
      '#..........................................#',
      '#...........##.......##...........G........#',
      '#...........##.....B.##....................#',
      '#...........##.......##....................#',
      '#..C............####V###V####..............#',
      '#...............##vvvvvvvvv##..............#',
      '#.......E.......##vvvvvvvvv##...........E..#',
      '#...............##vvvvvvvvv##.$............#',
      '#...............##vvvvvvvvv##..............#',
      '############################################',
    ],
  },
  {
    id: 'z4-lampyard',
    generators: ['gen_cocooncluster', 'gen_collapsedshaft', 'gen_casteridol'],
    patrols: ['glintmoth', 'delver', 'deep_caster'],
    map: [
      '############################################',
      '#..........................................#',
      '#.........................................F#',
      '#..@..........E.....F.........K.........E..#',
      '#.........##......##......##......##.......#',
      '#.........##......##......##......##.......#',
      '#.........##......##......##......##.......D',
      '#.........##......##......##......##.......#',
      '#.............................E............#',
      '#..........................................#',
      '#...................................B......#',
      '#.....................G....................#',
      '#................................###V#V#####',
      '#................................##vvvvvv###',
      '#.####LLL######.............F....##vvvvvv###',
      '#.#...........#..................##vvvvvv###',
      '#.#...........#.B................##vvvvvv###',
      '#.#........$..#..................##vvvvvv###',
      '#.#...........#..................##vvvvvv###',
      '#.#.....G.....#..................##vvvvvv###',
      '#.#...........#..................###########',
      '#.#...........#..............B.............#',
      '#.#...........#..E......G..................#',
      '#.#...........#............................#',
      '#.#############........................C...#',
      '#####################D######################',
    ],
  },
  {
    id: 'z4-lamplitrefuge',
    generators: [],
    patrols: [],
    map: [
      '#################S################',
      '#................................#',
      '#................................#',
      '#...#####..B..........B..#####...#',
      '#...#####................#####...#',
      '#...#####.....######.....#####...#',
      '#.............######.............#',
      '#................................#',
      '#..B.......A..........N.......B..#',
      '#................................#',
      '#................................#',
      '#.............######.............#',
      '#...#####.....######.....#####...#',
      '#...#####................#####...#',
      '#...#####..B..........B..#####...#',
      '#...............@................#',
      '#................................#',
      '##################################',
    ],
  },
]

// ============================================================================
// BOSS 4 — MOTHER OF LAMPS (DESIGN §5 Zone 4)
// "Verb: fight the light economy — the boss is only real where there is light."
// Every number the fight reads lives here; content/bosses/boss4.ts holds no
// literals but drawing geometry.
// ============================================================================

// ---------------------------------------------------------------- boss arena
// A circular chamber, 21 rows so the whole fight fits the camera, with four
// braziers at the compass points. They are ordinary 'B' tiles: the zone
// mechanic owns every brazier in the game and the boss asks it questions. The
// arena has no cover and no destructible parts — the only question in this room
// is which lights are burning.

export const ZONE4_BOSS_ARENA: RoomTemplate = {
  id: 'z4-motheroflamps',
  generators: [],
  patrols: [],
  map: [
      '############################################',
      '##########........................##########',
      '########............................########',
      '######................B...............######',
      '#####..................................#####',
      '####....................................####',
      '###......................................###',
      '###......................................###',
      '##........................................##',
      '##........................................##',
      '##......B..........................B......##',
      '##........................................##',
      '##........................................##',
      '###......................................###',
      '###......................................###',
      '####..................@.................####',
      '#####..................................#####',
      '######................B...............######',
      '########............................########',
      '##########........................##########',
      '#####################D######################',
  ],
}

/**
 * Boss tuning block. Arrays indexed by phase-1 (P1, P2, P3).
 *
 * FLAGGED INTERPRETATION (what "caught in a cone" means). DESIGN says each lit
 * brazier "projects a light-cone; caught in >=1 cone she is visible/vulnerable",
 * and is silent about the Warden's own lamp. Implemented as radii, not cones —
 * a brazier in a round room lights a circle, and a cone would need a facing the
 * fixture does not have — and the lamp is split from the braziers deliberately:
 *
 *   · inside a lit BRAZIER's radius  -> visible AND vulnerable
 *   · inside the Warden's LAMP only  -> visible (ghostly) but NOT vulnerable
 *   · outside both                   -> invisible and invulnerable
 *
 * That is the fight's whole thesis in one rule: light you must *build* is what
 * makes her real; light you *carry* only lets you watch. If the lamp made her
 * vulnerable the braziers would be decoration and the verb would collapse into
 * "walk at the boss".
 *
 * FLAGGED (approach-to-douse): P1/P2's douse is a fly-to-the-brazier, hover,
 * wingbeat. DESIGN gives the cadence, not the delivery. Making her come to the
 * light is what turns the douse into the player's DPS window and closes the
 * loop the verb needs: light -> she comes -> she is real -> burn her -> relight.
 * P3's all-douse is cast from wherever she is, which is exactly why P3 is the
 * pressure phase: the free windows stop arriving and you have to bait them.
 */
export const ZONE4_BOSS = {
  id: 'motheroflamps',
  name: 'MOTHER OF LAMPS',

  hp: 950,
  radius: 44,
  /** DESIGN §5 Boss 4: phases at 66% and 33%. */
  phase2At: 0.66,
  phase3At: 0.33,

  /** Chamber centre and the ring she drifts on when nothing is burning. */
  centre: { x: 22 * 32, y: 10 * 32 + 16 },
  driftRadius: 200,
  /**
   * …and the tighter ring she circles a LIT brazier on. Smaller than a brazier's
   * 160px reach on purpose: a moth-matriarch orbits the flame, which keeps her
   * inside the light she is circling and so keeps her real. Without this the
   * fight has no window at all in P3, where the wingbeat is cast from range.
   */
  hoverRadius: 96,
  driftSpeed: 0.42,

  /** How fast she crosses the chamber toward a brazier she means to eat. */
  approachSpeed: 190,

  douse: {
    /** Seconds between douses, per phase. DESIGN: 10s / (two at once) / 15s. */
    interval: [10, 9, 15],
    /** How many lights go out at once. P3's -1 means "all of them". */
    count: [1, 2, -1],
    /** The flutter before the wingbeat: a real telegraph, per phase. */
    windup: [1.1, 1.0, 1.6],
    /** P1/P2 fly to the brazier first; P3 does not (see the flag above). */
    approach: [true, true, false],
  },

  rush: {
    /** Seconds between rushes, per phase. */
    interval: [5.6, 4.8, 4.2],
    /** DESIGN §4: the tell survives every tier; P3 only tightens it. */
    windup: [0.85, 0.75, 0.65],
    speed: [560, 620, 680],
    damage: 17,
    /** How many rushes chain off one commitment, per phase (P3: 3x). */
    chain: [1, 2, 3],
    /** Breath between the links of a chain. */
    chainGap: 0.45,
    /** Half-width of the lane, px — the glint-trail's footprint. */
    halfWidth: 30,
  },

  /** P1 scatters Glintmoths; P2 sends Lampeaters at your relights. */
  spawn: {
    moths: { interval: [9, 11, 13], count: [4, 3, 3], body: 'glintmoth' },
    lampeaters: { interval: [0, 10, 12], count: [0, 1, 1], body: 'lampeater' },
    liveCap: 16,
  },

  /** P2's dust-storm ring: the lamp itself shrinks 30% (DESIGN). */
  dust: { lampMul: 0.7, ringSpeed: 0.9 },

  death: { shake: 15, shakeSeconds: 1.4, hitstop: 0.34 },
} as const

// ---------------------------------------------------------------- zone

export const ZONE4: ZoneConfig = {
  id: 4,
  name: 'The Gloaming Mines',
  tagline: 'Sight is a resource. Spend it, defend it, and do not stand still in the dark.',
  palette: ZONE4_PALETTE,
  rooms: ZONE4_ROOMS,
  roomCount: 3,
  // DESIGN §5 Zone 4: the modulation is the story, so the BASE is close to zone
  // 2's and the zone's real clock is what the player does with light — 2x in the
  // dark is a 45-second room, 0.5x in brazier light is a three-minute one.
  gloomFillSeconds: 90,
  vaultClockSeconds: 150,
  eliteRate: 0.12,
  eliteRiders: ['frenzied', 'shielded', 'gloomtouched'],
  // DESIGN §4 stat slope: +12% HP / +10% damage per zone, compounding. Zone 4 is
  // three steps past zone 1: 1.12^3 = 1.40, 1.10^3 = 1.33.
  statScale: { hp: 1.4, damage: 1.33 },
  // DESIGN §11's healing-scarcity curve: zone 1 gave 2-3, zone 2-3 gave 1-2,
  // here it is exactly one a room. Zone 5 will sell it to you.
  foodPerRoom: [1, 1],
  music: {
    root: 38,
    mode: 'phrygian',
    tempo: 62,
    padLevel: 1,
    bassLevel: 0.8,
    percLevel: 0.42,
    character: 'depths',
  },
  bossId: 'motheroflamps',
}
